// App.tsx — permanent Cognitory main shell (no splash overlay).
import { useEffect } from "react";
import { Container } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import FusionSun from "./components/FusionSun";
import CognitoryAuthPanel from "./components/CognitoryAuthPanel";
import SiteDeploymentStamp from "./components/SiteDeploymentStamp";
import { registerOperatorIdentityConsole } from "./auth/operatorIdentityConsole";
import { useCognitoryAuth } from "./auth/useCognitoryAuth";
import {
  MAIN_PAGE_FONT_STACK,
  MAIN_PAGE_PANEL_HEADING_STYLE,
  MAIN_PAGE_PANEL_INNER_STYLE,
} from "./theme/mainPageVisual";

declare global {
  interface Window {
    BUILD_INFO?: { commit?: string; builtAt?: string; version?: string };
  }
}

const FONT_STACK = MAIN_PAGE_FONT_STACK;

// Inject custom fade-in-up delays (kept, but done safely once)
function useInjectFadeStyles() {
  useEffect(() => {
    if (typeof document === "undefined") return;

    const id = "cognitory-fadeinup-style";
    if (document.getElementById(id)) return;

    const style = document.createElement("style");
    style.id = id;
    style.textContent = `
      .animate-fade-in {
        animation: fadeInUp 0.45s ease-out both;
      }
      .animate-fade-in.animate-delay-1 { animation-delay: 0.05s; }
      .animate-fade-in.animate-delay-2 { animation-delay: 0.1s; }
      .animate-fade-in.animate-delay-3 { animation-delay: 0.15s; }
      .animate-fade-in.animate-delay-4 { animation-delay: 0.2s; }
      .animate-fade-in.animate-delay-5 { animation-delay: 0.25s; }

      @keyframes fadeInUp {
        from { opacity: 0.001; transform: translateY(8px); }
        to   { opacity: 1; transform: translateY(0); }
      }

      @media (prefers-reduced-motion: reduce) {
        .animate-fade-in {
          animation: none !important;
          opacity: 1 !important;
          transform: none !important;
        }
      }
    `;
    document.head.appendChild(style);
  }, []);
}

type IntroMode = "signed_out" | "authenticated";

// Header — intro is a plain shell so Bootstrap brand nowrap
// styles cannot clip the copy. Signed-out literals stay in-source for LTDD locks.
function Header(props: { mode: IntroMode }) {
  const authenticated = props.mode === "authenticated";
  return (
    <header
      className="main-intro-shell"
      data-testid={
        authenticated
          ? "cognitory-intro-authenticated"
          : "cognitory-intro-signed-out"
      }
      style={{
        position: "relative",
        zIndex: 1,
        width: "100%",
        boxSizing: "border-box",
        padding:
          "clamp(1.75rem, 4vw, 3rem) clamp(1.25rem, 4vw, 2.5rem) clamp(1rem, 2.5vw, 1.5rem)",
        overflow: "visible",
      }}
    >
      <div
        className="main-intro"
        style={{
          width: "100%",
          maxWidth: "920px",
          margin: "0 auto",
          textAlign: "center",
          boxSizing: "border-box",
          whiteSpace: "normal",
          overflow: "visible",
          overflowWrap: "break-word",
          wordBreak: "normal",
        }}
      >
        <div
          className="animate-fade-in animate-delay-1 main-intro-label"
          style={{
            fontSize: "0.82rem",
            fontWeight: 500,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: "#4a5568",
            whiteSpace: "normal",
            fontFamily: FONT_STACK,
          }}
        >
          CognitoryIRB
        </div>
        <h1
          className="animate-fade-in animate-delay-2 main-intro-heading"
          style={{
            margin: "0.65rem auto 1rem",
            maxWidth: authenticated ? "16ch" : "18ch",
            fontWeight: 300,
            letterSpacing: "-0.03em",
            fontFamily: FONT_STACK,
            fontSize: "clamp(2rem, 4.2vw, 3.5rem)",
            lineHeight: 1.15,
            whiteSpace: "normal",
            overflow: "visible",
            textWrap: "balance",
            color: "#1a1a1a",
          }}
        >
          {authenticated
            ? "Ask your Cognitory."
            : "Ask your documents. Get an answer you can check."}
        </h1>
        <p
          className="animate-fade-in animate-delay-3 main-intro-body"
          style={{
            fontSize: "clamp(1.02rem, 1.6vw, 1.18rem)",
            color: "#555",
            margin: "0 auto",
            maxWidth: authenticated ? "30rem" : "36rem",
            fontWeight: 300,
            fontFamily: FONT_STACK,
            lineHeight: 1.65,
            whiteSpace: "normal",
            overflow: "visible",
          }}
        >
          {authenticated ? (
            <>
              Answers are grounded in the documents available to this Cognitory.
            </>
          ) : (
            <>
              If the documents support it, Cognitory shows you. If they
              don&apos;t, Cognitory says so. Answers stay grounded in your
              documents, with citations you can check—and no invented claims.
            </>
          )}
        </p>
        {!authenticated ? (
          <p
            className="animate-fade-in animate-delay-4 main-intro-support"
            style={{
              fontSize: "clamp(0.95rem, 1.4vw, 1.05rem)",
              color: "#4a5568",
              margin: "1rem auto 0",
              maxWidth: "32rem",
              fontWeight: 400,
              fontFamily: FONT_STACK,
              lineHeight: 1.55,
              whiteSpace: "normal",
              overflow: "visible",
            }}
          >
            {
              "It does not replace your judgment. It protects your judgment from unsupported AI output."
            }
          </p>
        ) : null}
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer
      className="text-center text-muted"
      style={{
        padding: "1.25rem 1rem 1.5rem",
        borderTop: "1px solid rgba(226, 232, 240, 0.9)",
        marginTop: "2rem",
      }}
      aria-label="Site footer"
    >
      <div style={{ fontSize: "0.9em", fontFamily: FONT_STACK }}>
        © {new Date().getFullYear()}{" "}
        <a
          href="https://cognitoryai.com"
          target="_blank"
          rel="noopener noreferrer"
        >
          Simonson Software
        </a>{" "}
        ·{" "}
        <a
          href="https://simonsonsoftware.com"
          target="_blank"
          rel="noopener noreferrer"
        >
          Simonson Software
        </a>
        . All rights reserved.
      </div>

      <div style={{ fontSize: "0.8em", marginTop: 8, fontFamily: FONT_STACK }}>
        <a
          href="mailto:psimonson@cognitoryai.com"
          className="link-secondary"
          style={{ textDecoration: "none", marginRight: 12 }}
        >
          Contact
        </a>
        <a
          href="https://cognitoryai.com/privacy"
          target="_blank"
          rel="noopener noreferrer"
          className="link-secondary"
          style={{ textDecoration: "none", marginRight: 12 }}
        >
          Privacy
        </a>
        <a
          href="https://cognitoryai.com/terms"
          target="_blank"
          rel="noopener noreferrer"
          className="link-secondary"
          style={{ textDecoration: "none" }}
        >
          Terms
        </a>
      </div>

      <SiteDeploymentStamp />
    </footer>
  );
}

export default function App() {
  useInjectFadeStyles();
  const auth = useCognitoryAuth();
  const introMode: IntroMode =
    auth.status === "signed_in" ? "authenticated" : "signed_out";

  // Development-only operator console for hash/UID comparison. No-op in production.
  useEffect(() => {
    registerOperatorIdentityConsole();
  }, []);

  return (
    <div
      data-test="app=home"
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        isolation: "isolate",
        background: "#f8fafc",
      }}
    >
      <FusionSun />
      <Header mode={introMode} />

      <div style={{ flex: "1 0 auto", position: "relative", zIndex: 1 }}>
        <Container
          style={{
            width: "100%",
            maxWidth: "720px",
            margin: "0 auto",
            paddingLeft: "clamp(1.25rem, 4vw, 2rem)",
            paddingRight: "clamp(1.25rem, 4vw, 2rem)",
            boxSizing: "border-box",
          }}
        >
          <div
            className="cognitory-select-panel"
            style={{
              padding:
                "clamp(1.75rem, 3.5vw, 2.35rem) clamp(1.25rem, 3.5vw, 2rem)",
              textAlign: "center",
              backgroundColor: "rgba(255, 255, 255, 0.25)",
              border: "1px solid rgba(148, 163, 184, 0.38)",
              borderRadius: "14px",
              boxShadow: "0 4px 18px rgba(15, 23, 42, 0.05)",
              backdropFilter: "blur(12px)",
              WebkitBackdropFilter: "blur(12px)",
              position: "relative",
              zIndex: 1,
              width: "100%",
              boxSizing: "border-box",
              overflow: "visible",
              marginBottom: "1.25rem",
            }}
          >
            <div
              className="cognitory-select-panel-inner"
              style={MAIN_PAGE_PANEL_INNER_STYLE}
            >
              {introMode === "signed_out" ? (
                <p
                  className="animate-fade-in animate-delay-4 cognitory-panel-heading"
                  style={MAIN_PAGE_PANEL_HEADING_STYLE}
                >
                  Sign in to open your Cognitory workspace
                </p>
              ) : null}

              <CognitoryAuthPanel />
            </div>
          </div>
        </Container>
      </div>

      <div style={{ position: "relative", zIndex: 1 }}>
        <Footer />
      </div>
    </div>
  );
}
